import { pydcApi } from './pydcApi';

export type SchemaIndexEntry = {
  schema_key: string;
  schema_profile: string | null;
  storage: 'central' | 'business';
  is_platform_key?: boolean;
  collection: string;
  missing_count: number;
  orphan_count?: number;
  total_count: number;
  defined_indexes?: Array<Record<string, unknown>>;
  missing_indexes?: Array<Record<string, unknown>>;
  existing_indexes?: Array<Record<string, unknown>>;
  orphan_indexes?: Array<
    { name?: string; key?: unknown; unique?: boolean } & Record<string, unknown>
  >;
  created_count?: number;
  failed_count?: number;
  skipped_count?: number;
  dropped_count?: number;
  dropped?: Array<Record<string, unknown>>;
  created?: Array<Record<string, unknown>>;
  failed?: Array<Record<string, unknown>>;
  skipped?: Array<Record<string, unknown>>;
  error?: string;
};

export type SchemaIndexStatusResult = {
  entries: SchemaIndexEntry[];
  schema_profiles: string[];
  total_entries: number;
  total_missing_indexes: number;
  total_orphan_indexes?: number;
};

export type SchemaIndexEnsureResult = {
  entries: SchemaIndexEntry[];
  schema_profiles: string[];
  total_entries: number;
  total_created: number;
  total_failed: number;
  total_skipped: number;
};

export type SchemaIndexDropOrphansResult = SchemaIndexEntry;

export type SchemaProfilesListResult = {
  schema_profiles: string[];
  count: number;
};

export type PlatformSchemaReloadResult = {
  dryRun: boolean;
  source: string;
  root: string;
  keys: string[];
  count: number;
  deleted?: number;
  loaded?: number;
};

export async function listSchemaIndexProfiles(): Promise<SchemaProfilesListResult> {
  const response = await pydcApi.get<SchemaProfilesListResult>('/admin/schema/indexes/profiles');
  return response.data;
}

export async function fetchSchemaIndexStatus(): Promise<SchemaIndexStatusResult> {
  const response = await pydcApi.get<SchemaIndexStatusResult>('/admin/schema/indexes/status');
  return response.data;
}

export async function ensureAllSchemaIndexes(): Promise<SchemaIndexEnsureResult> {
  const response = await pydcApi.post<SchemaIndexEnsureResult>('/admin/schema/indexes/ensure');
  return response.data;
}

export async function ensureSchemaIndexes(
  schemaKey: string,
  options?: { schemaProfile?: string; allProfiles?: boolean }
): Promise<SchemaIndexEnsureResult> {
  const params = new URLSearchParams();
  if (options?.schemaProfile) params.set('schemaProfile', options.schemaProfile);
  if (options?.allProfiles) params.set('allProfiles', 'true');
  const qs = params.toString();
  const response = await pydcApi.post<SchemaIndexEnsureResult>(
    `/admin/schema/indexes/${encodeURIComponent(schemaKey)}/ensure${qs ? `?${qs}` : ''}`
  );
  return response.data;
}

export async function dropSchemaOrphanIndexes(
  schemaKey: string,
  options?: { schemaProfile?: string }
): Promise<SchemaIndexDropOrphansResult> {
  const params = new URLSearchParams();
  if (options?.schemaProfile) params.set('schemaProfile', options.schemaProfile);
  const qs = params.toString();
  const response = await pydcApi.post<SchemaIndexDropOrphansResult>(
    `/admin/schema/indexes/${encodeURIComponent(schemaKey)}/drop-orphans${qs ? `?${qs}` : ''}`
  );
  return response.data;
}

export async function reloadPlatformSchema(
  dryRun: boolean
): Promise<PlatformSchemaReloadResult> {
  const response = await pydcApi.post<PlatformSchemaReloadResult>(
    '/admin/schema/platform/reload',
    { dryRun }
  );
  return response.data;
}
